// Cookie consent banner — AVG/GDPR compliant.
//
// Werkt als volgt:
//   1. Bij eerste bezoek: toon banner onderaan met "Accepteer" / "Weiger"
//   2. Keuze wordt opgeslagen in localStorage als 'abc-cookie-consent'
//   3. Banner verdwijnt na keuze, komt nooit meer terug (tenzij localStorage gewist)
//   4. Bij "Accepteer" → GA wordt geladen via analytics.js
//   5. Bij "Weiger" → geen tracking, alleen functionele cookies
//
// User kan zijn keuze altijd intrekken via de cookies-pagina.
//
// 2026-06-01 — banner-flicker fix: banner zit altijd in DOM, toggle via
// opacity + pointer-events. Voorkomt hydration-flash op mobiel (prerendered
// HTML kan banner WEL bevatten, client useState false → 1-frame flash).
// Lazy state initializer zorgt dat server- en client-render hetzelfde zien.

import { useEffect, useState } from 'react';

const CONSENT_KEY = 'abc-cookie-consent';
const VERSION = '1'; // bump versie als cookie-policy ingrijpend wijzigt

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || obj.version !== VERSION) return null;
    return obj.choice; // 'accepted' | 'rejected'
  } catch {
    return null;
  }
}

export function setCookieConsent(choice) {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ choice, version: VERSION, ts: Date.now() }));
    // Trigger custom event zodat analytics.js kan luisteren en GA kan laden
    window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: choice }));
  } catch { /* ignore */ }
}

export function CookieConsentBanner() {
  // Lazy initializer: bepaal show-state al tijdens eerste render zodat
  // prerendered HTML en client-side initial render dezelfde DOM produceren.
  // Voorkomt hydration-mismatch flash.
  const [show, setShow] = useState(() => {
    if (typeof window === 'undefined') return true; // SSR default: toon
    return getCookieConsent() === null;
  });

  // Na hydration nog een check (voor het zeldzame geval dat localStorage
  // tussen render en effect veranderd is, bv. via DevTools).
  useEffect(() => {
    if (getCookieConsent() !== null && show) setShow(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accept = () => { setCookieConsent('accepted'); setShow(false); };
  const reject = () => { setCookieConsent('rejected'); setShow(false); };

  // Banner ALTIJD in DOM — alleen opacity/pointer-events togglen. Dit
  // voorkomt mount/unmount cycles die op iOS Safari kunnen leiden tot
  // 1-frame flicker tijdens hydration en route-changes.
  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-hidden={!show}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9500,
        background: 'rgba(27, 42, 74, 0.97)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderTop: '2px solid var(--orange)',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.25)',
        padding: '20px 24px 24px',
        color: 'white',
        opacity: show ? 1 : 0,
        pointerEvents: show ? 'auto' : 'none',
        transform: show ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 0.28s ease, transform 0.28s ease',
        visibility: show ? 'visible' : 'hidden',
      }}
    >
      <div style={{
        maxWidth: 980,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 24,
        alignItems: 'center',
      }} className="cookie-banner-grid">
        <div>
          <div id="cookie-banner-title" style={{
            fontWeight: 800,
            fontSize: '1.05rem',
            marginBottom: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span aria-hidden="true">🍪</span> Cookies op deze site
          </div>
          <p style={{
            margin: 0,
            fontSize: '0.9rem',
            lineHeight: 1.55,
            color: 'rgba(255,255,255,0.85)',
          }}>
            Wij gebruiken functionele cookies (altijd) en optioneel analytische cookies (Google Analytics) om
            te begrijpen hoe de site gebruikt wordt. Geen advertentie- of tracking-cookies.{' '}
            <a href="/cookies/" style={{ color: 'var(--orange)', textDecoration: 'underline' }}>
              Meer info
            </a>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }} className="cookie-banner-buttons">
          <button
            onClick={reject}
            type="button"
            tabIndex={show ? 0 : -1}
            style={{
              padding: '10px 18px',
              background: 'transparent',
              color: 'white',
              border: '1.5px solid rgba(255,255,255,0.4)',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Alleen functioneel
          </button>
          <button
            onClick={accept}
            type="button"
            tabIndex={show ? 0 : -1}
            style={{
              padding: '10px 22px',
              background: 'var(--orange)',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontWeight: 800,
              fontSize: '0.9rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 14px rgba(232,93,4,0.4)',
            }}
          >
            Accepteer
          </button>
        </div>
      </div>
      <style>{`
        @media (max-width: 720px) {
          [aria-labelledby="cookie-banner-title"] .cookie-banner-grid {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
          }
          [aria-labelledby="cookie-banner-title"] .cookie-banner-buttons {
            justify-content: stretch !important;
          }
          [aria-labelledby="cookie-banner-title"] .cookie-banner-buttons button {
            flex: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}
